/**
 * MobileAuditLog – kompakte Kartenansicht fuer die System Historie
 */
import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../../App";
import { useNavigate } from "react-router-dom";
import {
  Shield, Search, ChevronLeft, Clock, User, Activity,
  AlertTriangle, AlertOctagon, CheckCircle2, Briefcase,
  ListTodo, Users, Calendar, Filter, X,
} from "lucide-react";
import { Input } from "../../components/ui/input";

const API = "/api";

const MOD = {
  zeiterfassung: { label: "Zeit", icon: Clock, color: "text-blue-400", bg: "bg-blue-500/15" },
  projekt: { label: "Projekt", icon: Briefcase, color: "text-indigo-400", bg: "bg-indigo-500/15" },
  aufgabe: { label: "Aufgabe", icon: ListTodo, color: "text-emerald-400", bg: "bg-emerald-500/15" },
  benutzer: { label: "User", icon: Users, color: "text-amber-400", bg: "bg-amber-500/15" },
  crm: { label: "CRM", icon: User, color: "text-purple-400", bg: "bg-purple-500/15" },
  system: { label: "System", icon: Shield, color: "text-gray-400", bg: "bg-gray-500/15" },
};

const SEV = {
  ok: { dot: "bg-green-500", bg: "border-green-500/20" },
  warning: { dot: "bg-amber-500", bg: "border-amber-500/30" },
  critical: { dot: "bg-red-500", bg: "border-red-500/30 bg-red-500/5" },
};

export default function MobileAuditLog({ onBack }) {
  const navigate = useNavigate();
  const goBack = onBack || (() => navigate(-1));
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "30" });
    if (activeFilter) params.append("module", activeFilter);
    if (searchText) params.append("search", searchText);
    const res = await authFetch(`${API}/audit-log?${params}`);
    if (res.ok) {
      const data = await res.json();
      if (page === 1) setEntries(data.entries || []);
      else setEntries(prev => [...prev, ...(data.entries || [])]);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    }
    setLoading(false);
  }, [page, activeFilter, searchText]);

  useEffect(() => { setPage(1); }, [activeFilter, searchText]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-full bg-[#0f1117] text-white" data-testid="mobile-audit-log">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f1117]/95 backdrop-blur border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={goBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white">
            <ChevronLeft className="w-4 h-4" />Zurueck
          </button>
          <h2 className="text-sm font-semibold flex items-center gap-1.5"><Shield className="w-4 h-4 text-indigo-400" />System Historie</h2>
          <button onClick={() => setShowSearch(!showSearch)} className="p-1.5 rounded-lg hover:bg-white/10">
            {showSearch ? <X className="w-4 h-4 text-gray-400" /> : <Search className="w-4 h-4 text-gray-400" />}
          </button>
        </div>

        {showSearch && (
          <div className="px-4 pb-3">
            <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Suche..." autoFocus
              className="bg-white/10 border-white/15 text-white text-sm placeholder:text-gray-500" data-testid="mobile-audit-search" />
          </div>
        )}

        {/* Quick filter buttons */}
        <div className="flex gap-1.5 px-4 pb-3 overflow-x-auto" data-testid="module-filters">
          <button onClick={() => setActiveFilter("")}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!activeFilter ? "bg-indigo-500 text-white" : "bg-white/10 text-gray-400"}`}>
            Alle ({total})
          </button>
          {Object.entries(MOD).map(([key, m]) => (
            <button key={key} onClick={() => setActiveFilter(key === activeFilter ? "" : key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${activeFilter === key ? "bg-indigo-500 text-white" : "bg-white/10 text-gray-400"}`}
              data-testid={`filter-${key}`}>
              <m.icon className="w-3 h-3" />{m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Entries */}
      <div className="px-4 py-3 space-y-2">
        {entries.length === 0 && !loading ? (
          <div className="text-center py-16 text-gray-600">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Keine Eintraege</p>
          </div>
        ) : (
          entries.map((e, i) => {
            const mod = MOD[e.module] || MOD.system;
            const sev = SEV[e.severity] || SEV.ok;
            const ModIcon = mod.icon;
            return (
              <div key={`${e.timestamp}-${i}`} className={`border ${sev.bg} border-white/10 rounded-xl p-3 transition-colors`}
                data-testid={`mobile-event-${i}`}>
                <div className="flex items-start gap-2.5">
                  <div className={`p-1.5 rounded-lg flex-shrink-0 mt-0.5 ${mod.bg}`}>
                    <ModIcon className={`w-3.5 h-3.5 ${mod.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-200">{e.action}</span>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sev.dot}`} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{e.description}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-600">
                      <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" />{e.user_name}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{e.date} {e.time}</span>
                      {e.project_name && <span className="truncate max-w-[100px]">{e.project_name}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {loading && (
          <div className="text-center py-4"><Activity className="w-5 h-5 animate-spin mx-auto text-gray-600" /></div>
        )}

        {page < pages && !loading && (
          <button onClick={() => setPage(p => p + 1)}
            className="w-full py-3 text-sm text-indigo-400 hover:text-indigo-300 text-center"
            data-testid="load-more-btn">
            Mehr laden...
          </button>
        )}
      </div>
    </div>
  );
}
